import { useContext, useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { config } from "../../Environment";
import useAuth from "../../hooks/useAuth";
import { WSContext } from "../../App";
import LoggerContext from "../../context/LoggerProvider";
import useRetryFetch from "../../hooks/useRetryFetch";
import { enrichVendorLogos } from "../../hooks/useProductSearch";
import useProcess from "../../hooks/useProcess";
import { toast } from "sonner";
import ProductCard from "./ProductCard";
import FilterBar from "./FilterBar";
import CategorySidebar from "./CategorySidebar";
import { Skeleton } from "../ui/skeleton";
import { ChevronLeft } from "lucide-react";
import { Button } from "../ui/button";
import { useZoneSidebar } from "../../context/ZoneSidebarContext";

interface Category {
  _id: string;
  name: string | { en: string; he?: string; de?: string };
  slug: string;
}

const CategoryPage = () => {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { Logger } = useContext(LoggerContext);
  const { socket } = useContext(WSContext);
  const { startProcess } = useProcess();
  const retryFetch = useRetryFetch();
  const { portalContainer, collapsed: zoneSidebarCollapsed } = useZoneSidebar();

  const [category, setCategory] = useState<Category | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategory, setLoadingCategory] = useState(true);

  // Filter state
  const [filters, setFilters] = useState({
    search: "",
    categories: [] as string[],
    tags: [] as string[],
    difficulty: [] as string[],
    estimatedTimeMin: undefined as number | undefined,
    estimatedTimeMax: undefined as number | undefined,
    priceMin: undefined as number | undefined,
    priceMax: undefined as number | undefined,
    vendorId: undefined as string | undefined,
  });

  // Helper to get localized name based on current language
  const getLocalizedName = useCallback((name: any): string => {
    if (typeof name === 'string') return name; // Legacy string format
    if (typeof name === 'object' && name !== null) {
      // Multi-language format
      const currentLang = i18n.language || 'en';
      if (currentLang === 'he' && name.he) return name.he;
      if (currentLang === 'de' && name.de) return name.de;
      return name.en || name.he || name.de || '';
    }
    return '';
  }, [i18n.language]);

  // Load category info and products
  useEffect(() => {
    if (categorySlug) {
      loadCategoryProducts(filters);
    }
  }, [categorySlug, i18n.language]);

  const loadCategoryProducts = useCallback(async (currentFilters: typeof filters) => {
    if (!categorySlug) return;

    setLoading(true);
    setLoadingCategory(true);

    const query = new URLSearchParams();
    query.set('type', 'activity');
    query.set('includeVendor', '1');
    query.set('language', i18n.language || 'en');

    // Add filters to API query
    if (currentFilters.search) {
      query.set('search', currentFilters.search);
    }
    if (currentFilters.tags.length > 0) {
      query.set('tags', currentFilters.tags.join(','));
    }
    if (currentFilters.difficulty.length > 0) {
      query.set('difficulty', currentFilters.difficulty.join(','));
    }
    if (currentFilters.estimatedTimeMin !== undefined) {
      query.set('estimatedTimeMin', currentFilters.estimatedTimeMin.toString());
    }
    if (currentFilters.estimatedTimeMax !== undefined) {
      query.set('estimatedTimeMax', currentFilters.estimatedTimeMax.toString());
    }
    if (currentFilters.priceMin !== undefined) {
      query.set('priceMin', currentFilters.priceMin.toString());
    }
    if (currentFilters.priceMax !== undefined) {
      query.set('priceMax', currentFilters.priceMax.toString());
    }
    if (currentFilters.vendorId) {
      query.set('vendorId', currentFilters.vendorId);
    }

    try {
      const response = await retryFetch(
        `${config.API_BASE}/api/products/category/${categorySlug}?${query.toString()}`
      );

      if (!response.ok) {
        throw new Error("Failed to load category products");
      }

      const result = await response.json();

      // Set category info
      if (result.category) {
        setCategory(result.category);
        setLoadingCategory(false);
      }

      // Set products (enrich with vendor logos)
      if (Array.isArray(result.list)) {
        const enrichedProducts = await enrichVendorLogos(result.list, retryFetch);
        setProducts(enrichedProducts);
      }
    } catch (error) {
      Logger.error("Failed to load category products", error);
      toast.error("Failed to load products");
      // Navigate back to marketplace if category not found
      if (error instanceof Error && error.message.includes("404")) {
        navigate("/marketplace");
      }
    } finally {
      setLoading(false);
    }
  }, [categorySlug, retryFetch, Logger, navigate, i18n.language]);

  const tryActivity = async (product: any) => {
    try {
      const query = new URLSearchParams();
      query.set('workflowId', product.data.activity.fiWorkflow);
      query.set('trialMode', 'true');
      const response = await retryFetch(`${config.API_BASE}/api/user/process/search?${query.toString()}`);
      const result = await response.json();

      if (Array.isArray(result.list) && result.list.length > 0) {
        toast.error("You already have an active trial for this activity. Please complete or purchase it.");
        return;
      }

      await startProcess({
        prescriptionId: product.data.activity.fiWorkflow,
        from: location,
        forward: location,
        trialMode: true,
        trialProductId: product._id,
        onError: () => {
          toast.error("An error occurred while starting the trial.");
        },
      });
    } catch (error) {
      Logger.error("Failed to start trial", error);
      toast.error("Failed to start trial. Please try again.");
    }
  };

  // Extract unique tags from all products
  const { availableCategories, availableTags, availableVendors } = useMemo(() => {
    const categoriesMap = new Map<string, any>();
    const tagsMap = new Map<string, any>();
    const vendorsMap = new Map<string, any>();

    products.forEach((product) => {
      if (Array.isArray(product.categories)) {
        product.categories.forEach((cat: any) => {
          const isObject = typeof cat === "object" && cat._id;
          if (isObject) {
            categoriesMap.set(cat._id, cat);
          } else {
            categoriesMap.set(cat, { _id: cat, name: { en: cat } });
          }
        });
      }
      if (Array.isArray(product.tags)) {
        product.tags.forEach((tag: any) => {
          const isObject = typeof tag === "object" && tag._id;
          if (isObject) {
            tagsMap.set(tag._id, tag);
          } else {
            tagsMap.set(tag, { _id: tag, name: { en: tag } });
          }
        });
      }
      if (product.fiVendor) {
        vendorsMap.set(product.fiVendor._id, {
          _id: product.fiVendor._id,
          displayName: product.fiVendor.displayName,
        });
      }
    });

    return {
      availableCategories: Array.from(categoriesMap.values()).sort((a, b) =>
        getLocalizedName(a.name).localeCompare(getLocalizedName(b.name))
      ),
      availableTags: Array.from(tagsMap.values()).sort((a, b) =>
        getLocalizedName(a.name).localeCompare(getLocalizedName(b.name))
      ),
      availableVendors: Array.from(vendorsMap.values()).sort((a, b) =>
        a.displayName.localeCompare(b.displayName)
      ),
    };
  }, [products, i18n.language, getLocalizedName]);

  const handleFilterChange = useCallback((newFilters: typeof filters) => {
    setFilters(newFilters);
    loadCategoryProducts(newFilters);
  }, [loadCategoryProducts]);

  const handleCategorySelect = (slug: string | null) => {
    if (slug) {
      navigate(`/marketplace/category/${slug}`);
    } else {
      navigate('/marketplace');
    }
  };

  return (
    <>
      {/* Portal CategorySidebar into ZoneSidebar */}
      {portalContainer && createPortal(
        <CategorySidebar
          selectedCategory={categorySlug}
          onCategorySelect={handleCategorySelect}
          collapsed={zoneSidebarCollapsed}
        />,
        portalContainer
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="preslist centercontainer py-6">
        {/* Back Button - Mobile */}
        <div className="lg:hidden mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/marketplace')}
            className="mb-2"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t("back_to_marketplace") || "Back to Marketplace"}
          </Button>
        </div>

        {/* Category Header */}
        {loadingCategory ? (
          <div className="mb-6">
            <Skeleton className="h-8 w-48 mb-2" />
          </div>
        ) : category ? (
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">{getLocalizedName(category.name)}</h1>
            <p className="text-gray-600 mt-1">
              {t("explore_category_products") || "Explore products in this category"}
            </p>
          </div>
        ) : null}

        {/* Filter Bar */}
        <FilterBar
          onFilterChange={handleFilterChange}
          availableCategories={availableCategories}
          availableTags={availableTags}
          availableVendors={availableVendors}
        />

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array(6)
              .fill(0)
              .map((_, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
                  <Skeleton className="h-40 w-full rounded-none bg-gray-200" />
                  <div className="p-5">
                    <Skeleton className="h-5 w-3/4 rounded bg-gray-200 mb-2" />
                    <Skeleton className="h-4 w-1/2 rounded bg-gray-200 mb-2" />
                    <div className="flex gap-1.5 mb-2">
                      <Skeleton className="h-5 w-16 rounded-full bg-gray-200" />
                      <Skeleton className="h-5 w-20 rounded-full bg-gray-200" />
                    </div>
                    <Skeleton className="h-4 w-full rounded bg-gray-200 mb-1" />
                    <Skeleton className="h-4 w-5/6 rounded bg-gray-200 mb-3" />
                    <Skeleton className="h-6 w-24 rounded bg-gray-200" />
                  </div>
                  <div className="p-4 bg-gray-50 flex gap-2 mt-auto">
                    <Skeleton className="h-9 flex-1 rounded bg-gray-200" />
                    <Skeleton className="h-9 flex-1 rounded bg-gray-200" />
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && products.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 mb-4 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <p className="text-gray-600 font-medium mb-1">
              {t("no_products_in_category") || "No products found in this category"}
            </p>
            <p className="text-gray-500 text-sm">
              {t("try_different_category") || "Try browsing other categories"}
            </p>
          </div>
        )}

        {/* Product Grid */}
        {!loading && products.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <ProductCard
                key={product._id}
                product={product}
                onTryNow={() => tryActivity(product)}
              />
            ))}
          </div>
        )}
        </div>
      </main>
    </>
  );
};

export default CategoryPage;
